/**
 * Ethereum-specific footer component displaying live ETH price + social links + token search
 * Only queries tokens_eth table and shows ETH price
 */

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useEthTokens } from '../../hooks/useEthTokens';

// Ethereum Brand Colors
const ETH_BLUE = '#627EEA';
const ETH_DARK = '#333333';
const ETH_LIGHT = '#FFFFFF';
const ETH_BLACK = '#000000';

const FooterContainer = styled.div`
  width: 100%;
  background: ${ETH_LIGHT};
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 12px 24px;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    flex-direction: column;
    gap: 12px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const PriceDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  color: ${ETH_DARK};
  font-size: 14px;
`;

const PriceLabel = styled.span`
  color: #666666;
  font-weight: 500;
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SocialLink = styled.a`
  color: #666666;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${ETH_BLUE};
    transform: translateY(-1px);
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: #F5F5F5;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  padding: 8px 16px 8px 36px;
  color: ${ETH_DARK};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  outline: none;
  transition: all 0.2s;
  
  &::placeholder {
    color: #999999;
  }
  
  &:focus {
    background: #FFFFFF;
    border-color: ${ETH_BLUE};
    box-shadow: 0 0 0 2px rgba(98, 126, 234, 0.1);
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999999;
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const ExpandedSearchPanel = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  bottom: calc(100% + 12px);
  right: 0;
  width: 400px;
  background: ${ETH_LIGHT};
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  padding: 16px;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  overflow-y: auto;
  z-index: 90;
  
  @media (max-width: 768px) {
    width: 100%;
    right: 0;
    left: 0;
    bottom: calc(100% + 12px);
  }
`;

const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #F5F5F5;
  }
`;

const TokenLogo = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TokenSymbol = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${ETH_DARK};
`;

const TokenName = styled.span`
  font-size: 11px;
  color: #666666;
`;

const EthPriceFooter: React.FC = () => {
  const [ethPrice, setEthPrice] = useState<number | null>(null);
  const [footerSearch, setFooterSearch] = useState('');

  // Get all ETH tokens for search
  const { tokens: allEthTokens } = useEthTokens('');

  // Native ETH address
  const NATIVE_ETH_ADDRESS = '0x0000000000000000000000000000000000000000';
  const ETH_CHAIN_ID = 1;

  // Filter tokens for search
  const filteredTokens = useMemo(() => {
    if (!footerSearch || footerSearch.length < 2) return [];
    const search = footerSearch.toLowerCase();
    return allEthTokens.filter(token =>
      token.name.toLowerCase().includes(search) ||
      token.symbol.toLowerCase().includes(search) ||
      token.contract_address?.toLowerCase().includes(search)
    ).slice(0, 5);
  }, [allEthTokens, footerSearch]);

  // Fetch ETH price from Relay Protocol API
  useEffect(() => {
    const fetchEthPrice = async () => {
      try {
        // Fetch native ETH price using Relay API
        const RELAY_API_BASE = 'https://api.relay.link';
        const url = `${RELAY_API_BASE}/currencies/token/price?address=${NATIVE_ETH_ADDRESS}&chainId=${ETH_CHAIN_ID}&referrer=mavrk.ink`;

        console.log('[EthPriceFooter] Fetching ETH price from:', url);
        const response = await fetch(url);

        if (!response.ok) {
          console.warn(`[EthPriceFooter] Relay API returned ${response.status} for ETH price`);
          return;
        }

        const data = await response.json();
        if (typeof data.price === 'number' && data.price > 0) {
          setEthPrice(data.price);
          console.log('[EthPriceFooter] ETH price:', data.price);
        } else {
          console.warn('[EthPriceFooter] Invalid price response:', data);
        }
      } catch (error) {
        console.error('[EthPriceFooter] Failed to fetch ETH price:', error);
      }
    };

    // Fetch immediately
    fetchEthPrice();

    // Refresh every 30 seconds
    const interval = setInterval(fetchEthPrice, 30000);

    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price: number | null): string => {
    if (price === null) return '...';
    return `$${price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const handleTokenClick = (token: any) => {
    window.location.href = `/eth/token/${token.contract_address}`;
  };

  return (
    <FooterContainer>
      <LeftSection>
        <PriceDisplay>
          <TokenLogo src="https://icons.llamao.fi/icons/chains/rsz_ethereum.jpg" alt="ETH" />
          <PriceLabel>ETH Price:</PriceLabel>
          <span>{formatPrice(ethPrice)}</span>
        </PriceDisplay>
        <SocialLinks>
          <SocialLink href="https://x.com/mavrkofficial" target="_blank" rel="noopener noreferrer">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </SocialLink>
          <SocialLink href="https://t.me/mavrkofficialannouncements" target="_blank" rel="noopener noreferrer">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 11.944 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
            </svg>
          </SocialLink>
        </SocialLinks>
      </LeftSection>

      <RightSection>
        <ExpandedSearchPanel $isOpen={footerSearch.length >= 2 && filteredTokens.length > 0}>
          {filteredTokens.map((token) => (
            <SearchResultItem key={token.contract_address} onClick={() => handleTokenClick(token)}>
              <TokenLogo src={token.logo_url || '/default-token.png'} alt={token.symbol} />
              <TokenInfo>
                <TokenSymbol>{token.symbol}</TokenSymbol>
                <TokenName>{token.name}</TokenName>
              </TokenInfo>
            </SearchResultItem>
          ))}
        </ExpandedSearchPanel>
        <SearchContainer>
          <SearchIconWrapper>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </SearchIconWrapper>
          <SearchInput
            type="text"
            placeholder="Search tokens..."
            value={footerSearch}
            onChange={(e) => setFooterSearch(e.target.value)}
          />
        </SearchContainer>
      </RightSection>
    </FooterContainer>
  );
};

export default EthPriceFooter;
